import { Panel } from '$tablet-app/src/app/components/Panel';
import { ScreenBackButton } from '$tablet-app/src/app/components/ScreenBackButton';
import { ScreenHeader } from '$tablet-app/src/app/components/ScreenHeader';
import { ScreenTitle } from '$tablet-app/src/app/components/ScreenTitle';
import Spacer from '$tablet-app/src/app/components/Spacer';
import { Recipe } from '$tablet-app/src/app/recipes/types';
import { palette } from '$tablet-app/src/app/theme';
import { RecipeIngredient } from '@akin-home/recipes';
import React, { FC } from 'react';
import { useWindowDimensions } from 'react-native';
import styled from 'styled-components/native';
import PreparationStepsGrid from '../PreparationStepsGrid';
import RecipeIngredientsView from './RecipeIngredientsView';
import RecipeOverviewView from './RecipeOverviewView';

interface Props {
  /** The recipe being viewed */
  recipe: Recipe;

  /** How many servings the recipe is for */
  servings?: number;

  /** Whether the recipe is a favourite */
  isFavourite?: boolean;

  /** This ingredients that are currently added to the shopping list */
  selected_ingredient_ids?: string[];

  /** Whether adding to the shopping list is currently enabled */
  enable_add_to_shopping_list?: boolean;

  /**
   * Callback for creating a shopping list.
   * This should only be present if the user has no main shopping list.
   */
  onPressCreateShoppingList?: () => void;
  onChangeServings?: (servings: number) => void;
  onToggleSelectedIngredient?: (ingredient: RecipeIngredient) => void;
  onPressFavourite?: () => void;
  onPressStartCooking?: () => void;
  onPressSwap?: () => void;
  onPressBack?: () => void;
  onPressAddAllIngredients?: () => void;
  onPressRemoveAllIngredients?: () => void;
}

const RecipeDetailsView: FC<Props> = (props) => {
  const {
    recipe,
    servings,
    isFavourite,
    selected_ingredient_ids,
    enable_add_to_shopping_list,
    onChangeServings,
    onPressFavourite,
    onPressSwap,
    onPressBack,
    onToggleSelectedIngredient,
    onPressAddAllIngredients,
    onPressRemoveAllIngredients,
    onPressCreateShoppingList,
  } = props;

  const { width } = useWindowDimensions();
  const isPortrait = width <= 600;

  return (
    <ScreenContainer>
      <Header>
        <ScreenBackButton onPress={onPressBack} />
        <ScreenTitle>Recipe</ScreenTitle>
      </Header>

      <Body>
        <MainPanel>
          <Columns>
            <RecipeOverviewView
              recipe={recipe}
              isFavourite={isFavourite}
              onPressFavourite={onPressFavourite}
              onPressSwap={onPressSwap}
            />

            {isPortrait ? (
              <Spacer width="100%" color="lightgrey" height="1px" />
            ) : (
              <ColumnSeparator />
            )}

            <RecipeIngredientsView
              recipe={recipe}
              servings={servings}
              isFavourite={isFavourite}
              selected_ingredient_ids={selected_ingredient_ids}
              enable_add_to_shopping_list={enable_add_to_shopping_list}
              onPressCreateShoppingList={onPressCreateShoppingList}
              onToggleSelectedIngredient={onToggleSelectedIngredient}
              onPressAddAllIngredients={onPressAddAllIngredients}
              onPressRemoveAllIngredients={onPressRemoveAllIngredients}
              onChangeServings={onChangeServings}
            />
          </Columns>

          <Spacer width="100%" color="lightgrey" height="1px" />

          <PreparationStepsGrid steps={recipe.prepNotes} />
        </MainPanel>
      </Body>
    </ScreenContainer>
  );
};

const ScreenContainer = styled.ScrollView`
  flex: 1;
`;

const Header = styled(ScreenHeader)`
  justify-content: flex-start;
  gap: 20px;
`;

const Body = styled.View`
  padding: 0 20px 20px;
`;

const MainPanel = styled(Panel)`
  padding: 40px;
  gap: 30px;
`;

const Columns = styled.View`
  flex-direction: row;
  flex-wrap: wrap;
  gap: 20px;
`;

const ColumnSeparator = styled.View`
  height: 100%;
  width: 1px;
  background-color: ${palette.Outline_blue};
`;

export default RecipeDetailsView;
